import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@suraasa/placebo-ui"
import { Dialog, DialogContent, theme } from "@suraasa/placebo-ui-legacy"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { IncompleteItemsCount } from "api/resources/learningItems/types"
import { APIError } from "api/utils"
import { pushIntoUserFeedbackQueue } from "firebaseInit"
import { CheckCircle, Circle, Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import routes from "routes"
import { handleErrors, mapErrorsV2 } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import Check from "../assets/icons/green-check.svg"
import CourseCompletion from "../Course/CourseCompletion"
import GetCourseCertificate from "../Course/GetCourseCertificate"

type Props = {
  courseName: string
  courseSlug: string
  onClose: () => void
  open: boolean
}

enum DialogType {
  none,
  pendingItems,
  certificate,
}

const PlannerCompleted = (props: Props) => {
  const navigate = useNavigate()
  const { slug } = useParams() as { slug: string }
  const { enrollments } = useEnrollments()
  const [searchParams] = useSearchParams()
  const plannerId = searchParams.get("plannerId")

  const [dialogType, setDialogType] = useState<DialogType>(DialogType.none)
  const [overview, setOverview] = useState<IncompleteItemsCount | null>(null)

  const handleClose = () => {
    setDialogType(DialogType.none)
    props.onClose()
  }

  useEffect(() => {
    const getOverview = async () => {
      try {
        const res = await api.learningItems.incompleteItemsCount({
          urlParams: {
            itemType: "course",

            itemId: plannerId!,
          },
        })
        setOverview(res)

        if (res.assessments === 0 && res.assignments === 0) {
          // triggerCourseFeedbackPrompt()
          setDialogType(DialogType.certificate)
        } else {
          setDialogType(DialogType.pendingItems)
        }
      } catch (e) {
        if (e instanceof APIError) handleErrors(e)
      }
    }

    if (props.open) {
      getOverview()
    }
  }, [plannerId, props.open])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const triggerCourseFeedbackPrompt = async () => {
    const checkIfFeedbackAlreadyExists = async () => {
      if (!plannerId) return
      try {
        const res = await api.feedback.retrieve({
          urlParams: {
            itemType: 1,
            itemId: plannerId,
          },
        })

        if (res) {
          return true
        }

        return false

        // This ensures that if a feedback already exists in the backend, we clear our local queue.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return false
      }
    }

    const exists = await checkIfFeedbackAlreadyExists()
    if (!exists) return

    if (plannerId)
      pushIntoUserFeedbackQueue({
        itemName: props.courseName,
        itemId: plannerId,
        itemType: "course",
      })
  }

  const course = enrollments?.find(item => item.slug === slug)
  const courseId = course?.id ?? null

  const certificateQuery = useQuery({
    enabled: courseId !== null && props.open,
    queryFn: () =>
      api.credentials.getUserCredentials({
        urlParams: { itemType: "course", itemId: courseId! },
      }),
    queryKey: queries.credentials.getUserCredentials("course", courseId)
      .queryKey,
    onError: err => {
      if (err instanceof APIError) {
        mapErrorsV2(err)
      }
    },
  })

  /**
   * A credential row can exist before its assets (image/PDF) are generated,
   * so `isSuccess` alone doesn't mean the certificate is actually ready to show.
   */
  const certificateReady =
    certificateQuery.isSuccess &&
    Boolean(certificateQuery.data.pdfFile || certificateQuery.data.highResImage)

  return (
    <>
      <Dialog
        open={dialogType === DialogType.pendingItems}
        onRequestClose={handleClose}
      >
        <DialogContent>
          <div className="px-1 py-2 sm:px-2 sm:py-3">
            <div className="mb-2 flex items-start justify-between">
              <img src={Check} alt="icon" />
              <IconButton onClick={handleClose} color="black">
                <Xmark />
              </IconButton>
            </div>
            <Typography variant="title1">Great Job!</Typography>
            <Typography className="mb-3">
              You have successfully completed the self-paced part of the course{" "}
              <b>{`"${props.courseName}"`}</b>.<br />
              <br />
              However, you still have some things left to complete.
            </Typography>
            {overview ? (
              [
                {
                  count: 0,
                  name: "Self-paced course",
                },
                {
                  count: overview.assessments,
                  name: "Assessments",
                },
                {
                  count: overview.assignments,
                  name: "Skill Evidences (Assignments)",
                },
                {
                  count: overview.classes,
                  name: "Classes",
                },
              ].map((item, idx) => (
                <div className="mb-1.5 flex items-center" key={idx}>
                  {item.count > 0 ? (
                    <Circle color={`${theme.colors.onSurface[200]}`} />
                  ) : (
                    <CheckCircle
                      color="white"
                      className="rounded-full bg-success-500"
                    />
                  )}
                  <Typography
                    variant="strong"
                    className="ms-1"
                    color={item.count > 0 ? "onSurface.500" : undefined}
                  >
                    {item.name}
                  </Typography>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center pt-3">
                <CircularProgress />
              </div>
            )}

            <Button
              color="black"
              style={{ borderRadius: "50px" }}
              className="mt-3 w-full"
              onClick={() =>
                navigate(routes.course.replace(":slug", props.courseSlug))
              }
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {certificateReady && (
        <GetCourseCertificate
          courseName={props.courseName}
          open={dialogType === DialogType.certificate}
          handleClose={handleClose}
          data={certificateQuery.data}
        />
      )}
      {(certificateQuery.isError ||
        (certificateQuery.isSuccess && !certificateReady)) && (
        <CourseCompletion
          open={dialogType === DialogType.certificate}
          handleClose={handleClose}
          courseName={props.courseName}
        />
      )}
    </>
  )
}

export default PlannerCompleted
